import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api/axiosInstance";
import getImageUrl from "../../utils/imageUtils";
import ConfirmModal from "../common/ConfirmModal";
import useDebounce from "../../hooks/useDebounce";
import styles from "../../styles/pages/user/BlockedUsersPage.module.css";
import { FaBan, FaUnlock, FaUser, FaSearch } from "react-icons/fa";
import { useToast } from "../../contexts/ToastContext";

const BlockedUsersSection = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [unblockingId, setUnblockingId] = useState(null);
  const [showUnblockModal, setShowUnblockModal] = useState(false);
  const [unblockUserId, setUnblockUserId] = useState(null);
  const [unblockUserName, setUnblockUserName] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 250);

  useEffect(() => {
    fetchBlockedUsers(debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  const fetchBlockedUsers = async (query = "") => {
    try {
      setLoading(true);
      const trimmedQuery = String(query || "").trim();
      const res = await api.get("/users/me/blocked", {
        params: trimmedQuery ? { q: trimmedQuery } : undefined,
      });
      const users = res.data.blockedUsers || [];
      setBlockedUsers(users);
    } catch (err) {
      console.error("Error fetching blocked users:", err);
      showToast({
        message: err.response?.data?.message || "Failed to load blocked users",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUnblock = (userId, userName) => {
    setUnblockUserId(userId);
    setUnblockUserName(userName);
    setShowUnblockModal(true);
  };

  const confirmUnblockUser = async () => {
    if (!unblockUserId) return;

    try {
      setUnblockingId(unblockUserId);
      setShowUnblockModal(false);
      await api.delete(`/users/${unblockUserId}/block`);
      await fetchBlockedUsers(searchQuery);
      showToast({
        message: "User unblocked successfully",
        type: "success",
      });
    } catch (err) {
      console.error("Error unblocking user:", err);
      showToast({
        message: err.response?.data?.message || "Failed to unblock user",
        type: "error",
      });
    } finally {
      setUnblockingId(null);
      setUnblockUserId(null);
      setUnblockUserName("");
    }
  };

  const handleViewProfile = (userId) => {
    if (userId) {
      navigate(`/user/${userId}`);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <div>Loading blocked users...</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '1rem' }}>
        <p style={{ color: '#6c757d', fontSize: '0.875rem', margin: '0.5rem 0 1rem 0' }}>
          Manage users you've blocked. Blocked users cannot follow you or see your posts.
        </p>
      </div>

      {blockedUsers.length > 0 && (
        <div className={styles.searchBar} style={{ marginBottom: '1rem' }}>
          <FaSearch className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search blocked users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      )}

      {blockedUsers.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
          <FaBan size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
          <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.125rem', fontWeight: '600' }}>No blocked users</h3>
          <p>You haven't blocked anyone yet. When you block someone, they'll appear here.</p>
        </div>
      ) : blockedUsers.length === 0 && searchQuery.trim() ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
          <FaSearch size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
          <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.125rem', fontWeight: '600' }}>No matches found</h3>
          <p>No blocked users match your search "{searchQuery}". Try a different search term.</p>
          <button
            onClick={() => setSearchQuery("")}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              background: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Clear search
          </button>
        </div>
      ) : (
        <div className={styles.list}>
          {blockedUsers.map((user) => (
            <div key={user._id} className={styles.userCard}>
              <div className={styles.userInfo}>
                <div
                  className={styles.avatarWrapper}
                  onClick={() => handleViewProfile(user._id)}
                >
                  {user.avatar ? (
                    <img
                      src={getImageUrl(user.avatar)}
                      alt={`${user.firstName} ${user.lastName}`}
                      className={styles.avatar}
                    />
                  ) : (
                    <div className={styles.avatarPlaceholder}>
                      <FaUser />
                    </div>
                  )}
                </div>
                <div className={styles.userDetails}>
                  <h3
                    className={styles.userName}
                    onClick={() => handleViewProfile(user._id)}
                  >
                    {user.firstName} {user.lastName}
                  </h3>
                  {user.username && (
                    <p className={styles.userUsername}>@{user.username}</p>
                  )}
                </div>
              </div>
              <button
                className={styles.unblockButton}
                onClick={() =>
                  handleUnblock(user._id, `${user.firstName} ${user.lastName}`)
                }
                disabled={unblockingId === user._id}
              >
                <FaUnlock />
                {unblockingId === user._id ? "Unblocking..." : "Unblock"}
              </button>
            </div>
          ))}
        </div>
      )}

      {showUnblockModal && (
        <ConfirmModal
          message={`Unblock ${unblockUserName}?`}
          onConfirm={confirmUnblockUser}
          onClose={() => setShowUnblockModal(false)}
        />
      )}
    </div>
  );
};

export default BlockedUsersSection;